import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getExplainability } from "../api/adminApi";
import {
  Section, StatCard, ShortId, fmtDate,
} from "../components/admin/AdminUI";

/**
 * Batch 5b-1 Section 1 — Explainability Detail Admin Page.
 *
 * Route: /admin/explainability/:ulid
 *
 * Renders the full breakdown of why the market engine emitted a
 * particular signal for a single listing:
 *   • Subject listing card.
 *   • Cohort widening path (every level the engine probed).
 *   • Market stats & price position.
 *   • Confidence breakdown (count_factor × dispersion_factor × level_max).
 *   • Sample of included comparables with `matched_reason`.
 *
 * Data source: GET /api/admin/explainability/{ulid}
 */
export default function AdminExplainabilityDetailPage() {
  const { ulid } = useParams();
  const q = useQuery({
    queryKey: ["admin", "explainability", ulid],
    queryFn: () => getExplainability(ulid),
    enabled: !!ulid,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <div className="text-zinc-400 p-6" data-testid="expl-loading">
        Lade Explainability für{" "}
        <span className="font-mono">{ulid?.slice(0, 12)}…</span>
      </div>
    );
  }
  if (q.isError) {
    const code = q.error?.response?.data?.error?.code;
    const message =
      q.error?.response?.data?.error?.message ||
      q.error?.message ||
      "Unbekannter Fehler";
    return (
      <div className="p-6 space-y-4" data-testid="expl-error">
        <div className="text-rose-300 text-sm">
          Fehler: <span className="font-mono">{code || "unknown"}</span>
        </div>
        <div className="text-zinc-400 text-sm">{message}</div>
        {code === "explainability_missing" && (
          <div className="text-amber-300 text-sm">
            Tipp: Trigger einen Market-Recompute via Admin Operations, dann
            ist der Block beim nächsten Aufruf vorhanden.
          </div>
        )}
        <Link
          to="/admin"
          className="text-xs text-zinc-400 hover:text-zinc-200"
          data-testid="expl-back-link"
        >
          ← zurück zum Admin
        </Link>
      </div>
    );
  }

  const data = q.data || {};
  const listing = data.listing || {};
  const expl = data.explainability || {};
  const summary = data.analysis_summary || {};
  const comparables = data.comparables_detail || [];
  const cohort = expl.cohort || {};
  const pp = expl.price_position || {};
  const sr = expl.signal_reasoning || {};
  const dq = expl.data_quality || {};
  const cb = sr.confidence_breakdown || {};
  const steps = cohort.widening_steps || [];

  return (
    <div className="space-y-6" data-testid="expl-page">
      {/* Header ----------------------------------------------------------- */}
      <div className="flex items-center justify-between" data-testid="expl-header">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Admin · Explainability
          </div>
          <h1 className="text-2xl text-zinc-100 mt-1">
            {listing.title || "Listing"}
          </h1>
          <div className="text-xs text-zinc-500 mt-1">
            <ShortId value={listing.ulid} /> ·{" "}
            {listing.source_platform || "—"} ·{" "}
            {listing.country?.toUpperCase() || "—"}{" "}
            ({listing.source_market || "—"})
          </div>
        </div>
        <Link
          to={`/listing/${listing.ulid}`}
          className="text-xs px-3 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="expl-listing-link"
        >
          Listing öffnen →
        </Link>
      </div>

      {/* Top stats -------------------------------------------------------- */}
      <Section
        title="Signal"
        testid="expl-signal-section"
        action={
          <span className="text-xs text-zinc-500">
            engine v{expl.version || "—"} · computed{" "}
            {fmtDate(summary.computed_at)}
          </span>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Target price"
            value={fmtEUR(pp.target_price_eur)}
            testid="expl-stat-target"
          />
          <StatCard
            label="Market median"
            value={fmtEUR(pp.market_median_eur)}
            hint={
              pp.market_p25_eur && pp.market_p75_eur
                ? `IQR ${fmtEUR(pp.market_p25_eur)} – ${fmtEUR(pp.market_p75_eur)}`
                : ""
            }
            testid="expl-stat-median"
          />
          <StatCard
            label="vs Market"
            value={
              pp.price_vs_market_pct == null
                ? "—"
                : `${pp.price_vs_market_pct > 0 ? "+" : ""}${pp.price_vs_market_pct.toFixed(1)}%`
            }
            hint={
              pp.price_difference_eur != null
                ? `${pp.price_difference_eur > 0 ? "+" : ""}${fmtEUR(pp.price_difference_eur)}`
                : ""
            }
            testid="expl-stat-vs-market"
          />
          <StatCard
            label="Signal"
            value={sr.signal || sr.signal_soft || "—"}
            hint={sr.caveat ? `Caveat: ${sr.caveat}` : ""}
            testid="expl-stat-signal"
          />
        </div>
        {sr.rationale && (
          <div
            className="mt-4 text-sm text-zinc-200 bg-zinc-950/60 border border-zinc-800 rounded-md p-3"
            data-testid="expl-rationale"
          >
            {sr.rationale}
          </div>
        )}
      </Section>

      {/* Cohort path ----------------------------------------------------- */}
      <Section title="Cohort Path" testid="expl-cohort-section">
        <div className="text-xs text-zinc-500 mb-3">
          Engine probiert die Levels von eng → breit, bricht beim ersten Level
          mit ausreichenden Daten ab. Aktuell akzeptiert:{" "}
          <span className="text-zinc-200 font-medium">
            {cohort.level_name || cohort.level || "—"}
          </span>
          {cohort.size != null && (
            <> — <span className="tabular-nums">{cohort.size}</span> Vergleichswerte.</>
          )}
        </div>
        {steps.length === 0 ? (
          <div className="text-xs text-zinc-500" data-testid="expl-steps-empty">
            Keine Widening-Steps verfügbar (Legacy-Run vor Section 1).
          </div>
        ) : (
          <ol className="space-y-2" data-testid="expl-widening-steps">
            {steps.map((s, i) => (
              <li
                key={`${s.level}-${i}`}
                data-testid={`expl-step-${i}`}
                className={`rounded-md border px-3 py-2 text-xs flex items-center justify-between gap-3 ${
                  s.status === "accepted"
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
                    : s.status === "insufficient"
                    ? "border-zinc-700 bg-zinc-900/50 text-zinc-400"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-200"
                }`}
              >
                <div className="flex-1">
                  <div className="text-zinc-200">
                    <span className="text-zinc-500 mr-2">#{i + 1}</span>
                    {s.level_name || s.level}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    {s.level}
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <div className="text-zinc-200">{s.size} Listings</div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">
                    {s.status}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
        {cohort.filter_summary && Object.keys(cohort.filter_summary).length > 0 && (
          <div
            className="mt-4 text-xs text-zinc-400 bg-zinc-950/40 border border-zinc-800 rounded-md p-3"
            data-testid="expl-filter-summary"
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Cohort filter
            </div>
            <pre className="text-zinc-300 whitespace-pre-wrap break-all text-[11px]">
              {JSON.stringify(cohort.filter_summary, null, 2)}
            </pre>
          </div>
        )}
      </Section>

      {/* Confidence breakdown ------------------------------------------- */}
      <Section title="Confidence breakdown" testid="expl-confidence-section">
        <div className="text-xs text-zinc-500 mb-3">
          Confidence ={" "}
          <span className="text-zinc-200">
            count_factor × dispersion_factor × level_max_confidence
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Count factor"
            value={fmtFloat(cb.count_factor)}
            hint={cb.count_saturates_at ? `saturiert ab n=${cb.count_saturates_at}` : ""}
            testid="expl-cb-count"
          />
          <StatCard
            label="Dispersion factor"
            value={fmtFloat(cb.dispersion_factor)}
            hint={
              pp.dispersion != null ? `dispersion=${fmtFloat(pp.dispersion)}` : ""
            }
            testid="expl-cb-dispersion"
          />
          <StatCard
            label="Level max"
            value={fmtFloat(cb.level_max_confidence)}
            hint={cohort.level_name || ""}
            testid="expl-cb-levelmax"
          />
          <StatCard
            label="Final score"
            value={fmtFloat(cb.final_score)}
            hint={`raw_product ${fmtFloat(cb.raw_product)}`}
            testid="expl-cb-final"
          />
        </div>
      </Section>

      {/* Comparables ---------------------------------------------------- */}
      <Section
        title={`Comparables (${comparables.length})`}
        testid="expl-comparables-section"
        action={
          <span className="text-xs text-zinc-500">
            Sample der Cohort (max 20)
          </span>
        }
      >
        {comparables.length === 0 ? (
          <div className="text-xs text-zinc-500" data-testid="expl-comps-empty">
            Keine Comparables im Sample — eventuell Legacy-Run ohne ULID-Sample.
          </div>
        ) : (
          <div className="space-y-2" data-testid="expl-comparables-list">
            {comparables.map((c, i) => (
              <ComparableRow
                key={c.listing?.ulid || i}
                index={i}
                entry={c}
                subjectVertical={listing.vertical}
              />
            ))}
          </div>
        )}
      </Section>

      {/* Data quality --------------------------------------------------- */}
      {(dq.kill_switch_enabled ||
        dq.series_match_level ||
        dq.perf_marker ||
        dq.perf_marker_mismatch) && (
        <Section title="Data quality flags" testid="expl-dq-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label="Kill-switch"
              value={dq.kill_switch_enabled ? "ENABLED" : "off"}
              testid="expl-dq-kill"
            />
            <StatCard
              label="Series match"
              value={dq.series_match_level || "—"}
              testid="expl-dq-series"
            />
            <StatCard
              label="Perf marker"
              value={dq.perf_marker || "—"}
              testid="expl-dq-perf"
            />
            <StatCard
              label="Perf mismatch"
              value={dq.perf_marker_mismatch ? "YES" : "no"}
              testid="expl-dq-mismatch"
            />
          </div>
        </Section>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Comparable row — listing summary + matched reason.
// --------------------------------------------------------------------------
function ComparableRow({ index, entry, subjectVertical }) {
  const lst = entry.listing || {};
  const det = entry.details || {};
  const reason = entry.matched_reason || "—";
  const summary = formatComparableSummary(det, subjectVertical);
  return (
    <div
      data-testid={`expl-comp-row-${index}`}
      className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
    >
      <div className="md:col-span-1 text-xs text-zinc-500 tabular-nums">
        #{index + 1}
      </div>
      <div className="md:col-span-5">
        <div className="text-sm text-zinc-200 truncate" title={lst.title}>
          {lst.title || "—"}
        </div>
        <div className="text-[10px] text-zinc-500 mt-0.5">
          <ShortId value={lst.ulid} /> · {lst.country?.toUpperCase() || "—"}{" "}
          ({lst.source_market || lst.source_platform || "—"})
        </div>
      </div>
      <div className="md:col-span-3 text-xs text-zinc-400">{summary}</div>
      <div className="md:col-span-2 text-right text-zinc-200 tabular-nums">
        {fmtEUR(lst.price)}
      </div>
      <div className="md:col-span-1 text-right">
        {lst.ulid && (
          <Link
            to={`/listing/${lst.ulid}`}
            className="text-xs text-sky-400 hover:text-sky-300"
            data-testid={`expl-comp-link-${index}`}
          >
            View
          </Link>
        )}
      </div>
      <div className="md:col-span-12 text-[11px] text-amber-200/80">
        match: {reason}
      </div>
    </div>
  );
}

function formatComparableSummary(det, vertical) {
  const parts = [];
  if (vertical === "auto") {
    if (det.year) parts.push(`${det.year}`);
    if (det.power_kw) parts.push(`${det.power_kw} kW`);
    if (det.fuel) parts.push(det.fuel);
    if (det.gearbox) parts.push(det.gearbox);
    if (det.body_type) parts.push(det.body_type);
  } else if (vertical === "real_estate") {
    if (det.object_type_canonical) parts.push(det.object_type_canonical);
    if (det.living_area_m2) parts.push(`${det.living_area_m2} m²`);
    if (det.rooms) parts.push(`${det.rooms} Zi`);
    if (det.postal_code) parts.push(det.postal_code);
    if (det.condition) parts.push(det.condition);
  }
  return parts.join(" · ") || "—";
}

function fmtEUR(v) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("de-AT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${v}`;
  }
}

function fmtFloat(v) {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toFixed(3);
}
